/**
 * Thumbs up/down (with an optional comment on "down") for one stored
 * assistant answer. Feeds the assistant's learning loop: negative comments
 * become lessons for similar requests of the organisation
 * (`ai-assistant-learning` spec, "Rating control in the panel"). Registered
 * as an `AgentMessageFooterComponent` in `views/messageFooters.ts`.
 */
import {
  ThumbDownAltOutlined as ThumbDownIcon,
  ThumbUpAltOutlined as ThumbUpIcon,
} from "@mui/icons-material";
import {
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import * as Sentry from "@sentry/react";
import React from "react";
import type { AgentMessageFooterComponent } from "./views/messageFooters.js";
import { useAgentFeedback } from "./useAgentFeedback.js";

export const AgentFeedback: AgentMessageFooterComponent = ({
  messageId,
  conversationId,
}) => {
  const { t } = useTranslate(["Assistant"]);
  const { ratings, store } = useAgentFeedback({ conversationId });
  const [editing, setEditing] = React.useState(false);
  const [comment, setComment] = React.useState("");
  const [note, setNote] = React.useState<"stored" | "withdrawn" | null>(null);
  const [error, setError] = React.useState(false);

  const rating = ratings[messageId]?.rating ?? null;

  const send = async (value: 1 | -1 | 0, text: string) => {
    setError(false);
    try {
      await store(messageId, value, text);
      setNote(value === 0 ? "withdrawn" : "stored");
      setEditing(false);
    } catch (err) {
      Sentry.captureException(err);
      setError(true);
    }
  };

  const helpfulLabel = t("This answer was helpful", { ns: "Assistant" });
  const notHelpfulLabel = t("This answer was not helpful – what was wrong?", {
    ns: "Assistant",
  });
  const withdrawLabel = t("Withdraw rating", { ns: "Assistant" });

  return (
    <Stack spacing={0.5} sx={{ mt: 0.75 }}>
      <Stack
        direction="row"
        spacing={0.25}
        useFlexGap
        sx={{ alignItems: "center", flexWrap: "wrap" }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          {t("Rate this answer:", { ns: "Assistant" })}
        </Typography>
        <Tooltip title={rating === 1 ? withdrawLabel : helpfulLabel}>
          <IconButton
            size="small"
            sx={{ minWidth: 44, minHeight: 44 }}
            aria-label={rating === 1 ? withdrawLabel : helpfulLabel}
            color={rating === 1 ? "primary" : "default"}
            onClick={() => void send(rating === 1 ? 0 : 1, "")}
          >
            <ThumbUpIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={rating === -1 ? withdrawLabel : notHelpfulLabel}>
          <IconButton
            size="small"
            sx={{ minWidth: 44, minHeight: 44 }}
            aria-label={rating === -1 ? withdrawLabel : notHelpfulLabel}
            color={rating === -1 ? "error" : "default"}
            onClick={() => {
              if (rating === -1) void send(0, "");
              else {
                setComment("");
                setEditing(true);
              }
            }}
          >
            <ThumbDownIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
        {!editing && note && !error && (
          <Typography variant="caption" color="text.secondary">
            {note === "withdrawn"
              ? t("Rating withdrawn.", { ns: "Assistant" })
              : t("Thanks – this will improve future answers.", {
                  ns: "Assistant",
                })}
          </Typography>
        )}
        {error && (
          <Typography variant="caption" color="error">
            {t("Could not save the rating", { ns: "Assistant" })}
          </Typography>
        )}
      </Stack>
      {editing && (
        <Stack
          direction="row"
          spacing={0.5}
          useFlexGap
          sx={{ alignItems: "flex-start", flexWrap: "wrap" }}
        >
          <TextField
            sx={{ flex: "1 1 12rem", minWidth: 0 }}
            size="small"
            multiline
            minRows={1}
            maxRows={4}
            label={t("What should have been different?", {
              ns: "Assistant",
            })}
            value={comment}
            onChange={event => setComment(event.target.value)}
          />
          <Button size="small" onClick={() => void send(-1, comment.trim())}>
            {t("Send feedback", { ns: "Assistant" })}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
